// Content hierarchy types and data
export interface Task {
  id: string;
  sort: number;
  title: string;
  description: string;
  premium?: boolean;
  individualCompletionRequired?: boolean;
}

export interface Module {
  [moduleName: string]: Task[];
}

export interface ContentHierarchy {
  [stageName: string]: Module;
}

// Import the content hierarchy data
export const contentHierarchy: ContentHierarchy = {
  Explore: {
    Ideation: [
      {
        id: 'select-idea',
        sort: 0,
        title: 'Select Your Idea',
        description: 'Choose from AI-generated ideas or enter your own.',
        individualCompletionRequired: true,
      },
      {
        id: 'validate-market-size',
        sort: 1,
        title: 'Validate Market Size',
        description: 'Research and estimate the total addressable market for your idea.',
        individualCompletionRequired: true,
      },
      {
        id: 'identify-competitors',
        sort: 2,
        title: 'Identify Competitors',
        description: 'Research existing solutions and analyze competitive landscape.',
        individualCompletionRequired: true,
      },
      {
        id: 'conduct-user-interviews',
        sort: 3,
        title: 'Conduct User Interviews',
        description: 'Interview potential users to validate problem and solution fit.',
        individualCompletionRequired: true,
      },
      {
        id: 'define-ideal-customer-profile',
        sort: 4,
        title: 'Define Ideal Customer Profile (ICP)',
        description:
          'An ICP is the segment of customers who are the best fit for your product. Lets discover who they are!',
        individualCompletionRequired: true,
      },
      {
        id: 'create-mockup-prototype',
        sort: 5,
        title: 'Create Mockup/Prototype',
        description: 'Build a simple mockup or prototype to visualize your solution.',
        individualCompletionRequired: true,
      },
    ],
    Team: [
      {
        id: 'invite-collaborators',
        sort: 6,
        title: 'Invite Collaborators',
        description: 'Bring in cofounders or teammates early.',
        premium: true,
      },
      {
        id: 'assign-roles',
        sort: 7,
        title: 'Assign Roles',
        description: "Clarify who's doing what.",
      },
      {
        id: 'equity-planning',
        sort: 8,
        title: 'Equity Planning',
        description: 'Sketch out how equity might be split.',
        premium: true,
      },
    ],
  },
  Incubation: {
    Strategize: [
      {
        id: 'review-pitch',
        sort: 9,
        title: 'Review Pitch',
        description: 'Edit or generate your elevator pitch.',
      },
      {
        id: 'problem',
        sort: 10,
        title: 'Problem',
        description: "Describe the main problem you're solving.",
      },
      {
        id: 'solution',
        sort: 11,
        title: 'Solution',
        description: 'Summarize how your product solves the problem.',
      },
      {
        id: 'audience',
        sort: 12,
        title: 'Audience',
        description: 'Define who your target users are.',
      },
      {
        id: 'differentiator',
        sort: 13,
        title: 'Differentiator',
        description: 'Highlight what makes you unique.',
      },
      {
        id: 'canvas-overview',
        sort: 14,
        title: 'Canvas Overview',
        description: 'Fill out the business canvas to structure your plan.',
      },
      {
        id: 'value-prop',
        sort: 15,
        title: 'Value Prop',
        description: 'Clarify the core value you offer.',
      },
      {
        id: 'revenue-cost',
        sort: 16,
        title: 'Revenue & Cost',
        description: 'Sketch your monetization and cost structure.',
      },
      {
        id: 'customer-segments',
        sort: 17,
        title: 'Customer Segments',
        description: 'Identify who benefits from your solution.',
      },
    ],
    Validate: [
      {
        id: 'what-to-validate',
        sort: 18,
        title: 'What to Validate',
        description: 'Determine which assumptions need testing.',
      },
      {
        id: 'design-experiments',
        sort: 19,
        title: 'Design Experiments',
        description: 'Plan experiments to validate key assumptions.',
        premium: true,
      },
      {
        id: 'run-interviews',
        sort: 20,
        title: 'Run Interviews',
        description: 'Gather feedback through surveys or conversations.',
      },
      {
        id: 'launch-waitlist',
        sort: 21,
        title: 'Launch a Waitlist',
        description: 'Test demand with a landing page or form.',
      },
      {
        id: 'analyze-learnings',
        sort: 22,
        title: 'Analyze Learnings',
        description: 'Review what your validation taught you.',
        premium: true,
      },
    ],
    Funding: [
      {
        id: 'funding-needs',
        sort: 23,
        title: 'Funding Needs',
        description: "Estimate how much capital you'll need.",
      },
      {
        id: 'funding-sources',
        sort: 24,
        title: 'Funding Sources',
        description: 'List where funding might come from.',
        premium: true,
      },
      {
        id: 'initial-budget',
        sort: 25,
        title: 'Initial Budget',
        description: 'Sketch a basic startup budget.',
        premium: true,
      },
    ],
    Incorporation: [
      {
        id: 'legal-entity-type',
        sort: 26,
        title: 'Legal Entity Type',
        description: 'Decide what kind of business entity to form.',
      },
      {
        id: 'state-incorporation',
        sort: 27,
        title: 'State of Incorporation',
        description: "Choose where you'll incorporate your business.",
      },
      {
        id: 'register-company',
        sort: 28,
        title: 'Register Your Company',
        description: 'Begin the filing process and get legal recognition.',
        premium: true,
      },
    ],
    Compliance: [
      {
        id: 'privacy-terms',
        sort: 29,
        title: 'Privacy & Terms',
        description: 'Generate or upload privacy policy and terms of service.',
        premium: true,
      },
      {
        id: 'data-compliance',
        sort: 30,
        title: 'Data Compliance',
        description: 'Identify any legal or regulatory data requirements.',
        premium: true,
      },
      {
        id: 'ip-protection',
        sort: 31,
        title: 'IP Protection',
        description: 'Plan trademarks, patents, or copyright protections.',
        premium: true,
      },
    ],
    'Founder Readiness': [
      {
        id: 'time-commitment',
        sort: 32,
        title: 'Time Commitment',
        description: 'Clarify your availability to work on the business.',
        individualCompletionRequired: true,
      },
      {
        id: 'skill-inventory',
        sort: 33,
        title: 'Skill Inventory',
        description: 'Identify skills you have and gaps to fill.',
        individualCompletionRequired: true,
      },
      {
        id: 'long-term-vision',
        sort: 34,
        title: 'Long-Term Vision',
        description: 'Think through where you want to take this.',
        individualCompletionRequired: true,
      },
    ],
  },
  Launch: {
    Build: [
      {
        id: 'define-mvp',
        sort: 35,
        title: 'Define MVP',
        description: 'Outline the smallest thing worth launching.',
      },
      {
        id: 'list-features',
        sort: 36,
        title: 'List Features',
        description: "Document features you'll include.",
      },
      {
        id: 'choose-tools',
        sort: 37,
        title: 'Choose Tools',
        description: "Pick how you'll build it (tech, no-code, etc).",
      },
      {
        id: 'roadmap-milestones',
        sort: 38,
        title: 'Roadmap Milestones',
        description: 'Plan key build stages.',
      },
      {
        id: 'capture-decisions',
        sort: 39,
        title: 'Capture Decisions',
        description: 'Note major product or technical choices.',
        premium: true,
      },
    ],
    Market: [
      {
        id: 'pick-activities',
        sort: 40,
        title: 'Pick Activities',
        description: 'Select which marketing actions make sense.',
      },
      {
        id: 'landing-page-copy',
        sort: 41,
        title: 'Landing Page Copy',
        description: 'Draft messaging for your site (if needed).',
      },
      {
        id: 'channels-explore',
        sort: 42,
        title: 'Channels to Explore',
        description: 'Choose potential marketing channels.',
      },
      {
        id: 'content-snippets',
        sort: 43,
        title: 'Content Snippets',
        description: 'Draft CTAs or launch content.',
      },
      {
        id: 'launch-plan',
        sort: 44,
        title: 'Launch Plan',
        description: 'Outline your initial go-to-market steps.',
      },
    ],
    Monetize: [
      {
        id: 'revenue-model',
        sort: 45,
        title: 'Revenue Model',
        description: 'Choose how your business earns money.',
      },
      {
        id: 'pricing-strategy',
        sort: 46,
        title: 'Pricing Strategy',
        description: 'Sketch your pricing tiers or value plan.',
      },
      {
        id: 'payment-triggers',
        sort: 47,
        title: 'Payment Triggers',
        description: 'Define what users pay for and when.',
      },
      {
        id: 'revenue-summary',
        sort: 48,
        title: 'Revenue Summary',
        description: 'Review your monetization approach.',
        premium: true,
      },
    ],
    GoToMarket: [
      {
        id: 'positioning',
        sort: 49,
        title: 'Positioning',
        description: "Clarify how you're positioned against alternatives.",
      },
      {
        id: 'launch-campaign',
        sort: 50,
        title: 'Launch Campaign',
        description: 'Plan your first launch effort.',
        premium: true,
      },
      {
        id: 'distribution-channels',
        sort: 51,
        title: 'Distribution Channels',
        description: 'Define how users will find and access you.',
        premium: true,
      },
    ],
  },
  Growth: {
    Analytics: [
      {
        id: 'set-success-metrics',
        sort: 52,
        title: 'Set Success Metrics',
        description: 'Decide what success looks like and how to measure it.',
      },
      {
        id: 'enable-analytics',
        sort: 53,
        title: 'Enable Analytics',
        description: 'Connect tracking tools like GA4 or Plausible.',
        premium: true,
      },
      {
        id: 'collect-feedback',
        sort: 54,
        title: 'Collect Feedback',
        description: 'Add ways for users to provide feedback post-launch.',
        premium: true,
      },
    ],
  },
};

// Helper functions
export const getAllTasks = (): Task[] => {
  return Object.values(contentHierarchy)
    .flatMap((stage) => Object.values(stage))
    .flat()
    .sort((a, b) => a.sort - b.sort);
};

export const getTaskById = (taskId: string): Task | undefined => {
  return getAllTasks().find((task) => task.id === taskId);
};

export const getTaskStage = (taskId: string): string => {
  for (const [stageName, modules] of Object.entries(contentHierarchy)) {
    for (const [_moduleName, tasks] of Object.entries(modules)) {
      if (tasks.some((task) => task.id === taskId)) {
        return stageName;
      }
    }
  }
  return 'Unknown';
};

export const getTaskModule = (taskId: string): string => {
  for (const [_stageName, modules] of Object.entries(contentHierarchy)) {
    for (const [moduleName, tasks] of Object.entries(modules)) {
      if (tasks.some((task) => task.id === taskId)) {
        return moduleName;
      }
    }
  }
  return 'Unknown';
};
